import { useState } from 'react'
import { lumi } from '@/lib/lumi'

export interface Calculation {
  _id?: string
  calculationType: 'personal' | 'business' | 'lifestyle' | 'wedding' | 'pet' | 'fashion' | 'digital' | 'student'
  totalEmissions: number
  breakdown: Record<string, number>
  equivalencies?: Record<string, number>
  inputs?: Record<string, any>
  notes?: string
  userName?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface BusinessReport {
  _id?: string
  companyName: string
  reportPeriod: string
  scope1Emissions: number
  scope2Emissions: number
  scope3Emissions?: number
  totalEmissions: number
  detailedBreakdown: Record<string, number>
  inputs: Record<string, any>
  employeeCount?: number
  industry?: string
  creator: string
  createdAt: string
  updatedAt: string
}

export interface SavedProfile {
  _id?: string
  profileName: string
  profileType: 'personal' | 'business' | 'lifestyle' | 'wedding' | 'pet' | 'fashion' | 'digital' | 'student'
  defaultInputs: Record<string, any>
  description?: string
  lastUsed: string
  creator: string
  createdAt: string
  updatedAt: string
}

export const useCalculations = () => {
  const [calculations, setCalculations] = useState<Calculation[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)

  const fetchCalculations = async (filter?: Record<string, any>) => {
    setLoading(true)
    try {
      const result = await lumi.entities.calculations.list({
        filter: filter || {},
        sort: { createdAt: -1 },
        limit: 50
      })
      setCalculations(result.list)
      setTotal(result.total)
    } catch (error) {
      console.error('Failed to fetch calculations:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveCalculation = async (data: Omit<Calculation, '_id' | 'creator' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const calculation = await lumi.entities.calculations.create({
        ...data,
        creator: 'current-user',
        createdAt: now,
        updatedAt: now
      })
      await fetchCalculations()
      return calculation
    } catch (error) {
      console.error('Failed to save calculation:', error)
      throw error
    }
  }

  const deleteCalculation = async (id: string) => {
    try {
      await lumi.entities.calculations.delete(id)
      await fetchCalculations()
    } catch (error) {
      console.error('Failed to delete calculation:', error)
      throw error
    }
  }

  return {
    calculations,
    loading,
    total,
    fetchCalculations,
    saveCalculation,
    deleteCalculation
  }
}

export const useBusinessReports = () => {
  const [reports, setReports] = useState<BusinessReport[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)

  const fetchReports = async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.business_reports.list({
        sort: { createdAt: -1 },
        limit: 50
      })
      setReports(result.list)
      setTotal(result.total)
    } catch (error) {
      console.error('Failed to fetch reports:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveReport = async (data: Omit<BusinessReport, '_id' | 'creator' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const report = await lumi.entities.business_reports.create({
        ...data,
        creator: 'current-user',
        createdAt: now,
        updatedAt: now
      })
      await fetchReports()
      return report
    } catch (error) {
      console.error('Failed to save report:', error)
      throw error
    }
  }

  const deleteReport = async (id: string) => {
    try {
      await lumi.entities.business_reports.delete(id)
      await fetchReports()
    } catch (error) {
      console.error('Failed to delete report:', error)
      throw error
    }
  }

  return {
    businessReports: reports,
    loading,
    total,
    fetchBusinessReports: fetchReports,
    saveBusinessReport: saveReport,
    deleteBusinessReport: deleteReport
  }
}

export const useSavedProfiles = () => {
  const [profiles, setProfiles] = useState<SavedProfile[]>([])
  const [loading, setLoading] = useState(false)

  const fetchProfiles = async (profileType?: string) => {
    setLoading(true)
    try {
      const filter = profileType ? { profileType } : {}
      const result = await lumi.entities.saved_profiles.list({
        filter,
        sort: { lastUsed: -1 },
        limit: 20
      })
      setProfiles(result.list)
    } catch (error) {
      console.error('Failed to fetch profiles:', error)
    } finally {
      setLoading(false)
    }
  }

  const saveProfile = async (data: Omit<SavedProfile, '_id' | 'creator' | 'createdAt' | 'updatedAt'>) => {
    try {
      const now = new Date().toISOString()
      const profile = await lumi.entities.saved_profiles.create({
        ...data,
        creator: 'current-user',
        createdAt: now,
        updatedAt: now
      })
      await fetchProfiles()
      return profile
    } catch (error) {
      console.error('Failed to save profile:', error)
      throw error
    }
  }

  const updateProfileLastUsed = async (id: string) => {
    try {
      const now = new Date().toISOString()
      await lumi.entities.saved_profiles.update(id, {
        lastUsed: now,
        updatedAt: now
      })
      await fetchProfiles()
    } catch (error) {
      console.error('Failed to update profile:', error)
    }
  }

  const deleteProfile = async (id: string) => {
    try {
      await lumi.entities.saved_profiles.delete(id)
      await fetchProfiles()
    } catch (error) {
      console.error('Failed to delete profile:', error)
      throw error
    }
  }

  return {
    profiles,
    loading,
    fetchProfiles,
    saveProfile,
    updateProfileLastUsed,
    deleteProfile
  }
}